"use client";
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Search, Filter, Plus, Grid3X3, ChevronDown, Package, BarChart3,
  Layers, Table, AlertTriangle, CheckCircle2, XCircle, Edit3Icon,
  Trash2Icon, CheckCircle,
} from 'lucide-react';
import ProductCard from './ProductCard';
import { useNavigate } from 'react-router-dom';
import { useProducts } from "../context/ProductsContext";
import { useCategories } from "../context/CategoryContext";
import { useSubCategories } from "../context/SubcategoryContext";
import { useModels } from "../context/ModelContext";

const strEq = (a, b) => String(a) === String(b);

/* ─── Stock Badge Helper ─── */
const StockBadge = ({ stock }) => {
  const qty = Number(stock);
  if (qty === 0)
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-600">
        <XCircle size={11} /> Out of Stock
      </span>
    );
  if (qty <= 10)
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-600">
        <AlertTriangle size={11} /> Low ({qty})
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
      <CheckCircle2 size={11} /> {qty} in stock
    </span>
  );
};

/* ─── Toggle Switch Helper ─── */
const ToggleSwitch = ({ isActive, onToggle, productId }) => (
  <button
    onClick={() => onToggle(productId)}
    title={isActive ? "Set Inactive" : "Set Active"}
    className={`relative inline-flex items-center w-9 h-5 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 ${
      isActive ? "bg-emerald-500 focus:ring-emerald-400" : "bg-gray-300 focus:ring-gray-400"
    }`}
  >
    <span
      className={`inline-block w-3.5 h-3.5 bg-white rounded-full shadow transform transition-transform duration-200 ${
        isActive ? "translate-x-4" : "translate-x-0.5"
      }`}
    />
  </button>
);

/* ─── Action Buttons Helper ─── */
const ActionButtons = ({ product, onEdit, onToggle, onDelete }) => (
  <div className="flex items-center gap-1.5">
    <ToggleSwitch isActive={product.status === "Active"} onToggle={onToggle} productId={product.id} />
    <button
      onClick={() => onEdit(product)}
      title="Edit"
      className="p-2 rounded-lg text-teal-700 hover:text-teal-600 hover:bg-teal-50 transition-all"
    >
      <Edit3Icon size={15} />
    </button>
    <button
      onClick={() => onDelete(product.id)}
      title="Delete"
      className="p-2 rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 transition-all"
    >
      <Trash2Icon size={15} />
    </button>
  </div>
);

const ProductPage = () => {
  const navigate = useNavigate();
  const { categories = [], fetchCategories, loading: categoriesLoading } = useCategories();
  const { subCategories = [], fetchSubCategories, loading: subCategoriesLoading } = useSubCategories();
  const { models, fetchModels, loading: modelsLoading } = useModels();
  const { products, loading, error, addProduct, toggleProductStatus, updateProduct, deleteProduct } = useProducts();

  const safeProducts = Array.isArray(products) ? products : [];

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [subCategoryFilter, setSubCategoryFilter] = useState('all');
  const [stockFilter, setStockFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [viewMode, setViewMode] = useState('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [activeDropdown, setActiveDropdown] = useState(null);

  /* ── Toast State ── */
  const [toast, setToast] = useState({ visible: false, message: '', type: 'success' });

  const showToast = useCallback((message = 'Status updated successfully!', type = 'success') => {
    setToast({ visible: true, message, type });
    setTimeout(() => setToast((t) => ({ ...t, visible: false })), 3000);
  }, []);

  useEffect(() => {
    fetchCategories?.().catch(err => console.warn('Failed to fetch categories', err));
    fetchSubCategories?.().catch(err => console.warn('Failed to fetch sub-categories', err));
  }, [fetchCategories, fetchSubCategories]);

  // ── EFFECTIVE STATUS ─────────────────────────────────────────────────────────
  const getEffectiveStatus = (product) => {
    const catId = product.category_id     ?? product.categoryId     ?? product.categoryID    ?? '';
    const subId = product.sub_category_id ?? product.subcategory_id ?? product.subCategoryId ?? '';
    const cat   = categories.find(c => String(c.id) === String(catId));
    const sub   = subCategories.find(s => String(s.id) === String(subId));
    if (cat?.status === 'Inactive' || sub?.status === 'Inactive') return 'Inactive';
    return product.status;
  };

  // ── STATUS OPTIONS ───────────────────────────────────────────────────────────
  const statusOptions = useMemo(() => [
    { value: 'all',      label: 'All Products', count: safeProducts.length },
    { value: 'Active',   label: 'Active',       count: safeProducts.filter(p => getEffectiveStatus(p) === 'Active').length },
    { value: 'Inactive', label: 'Inactive',     count: safeProducts.filter(p => getEffectiveStatus(p) === 'Inactive').length },
  ], [safeProducts, categories, subCategories]);

  // ── CATEGORY OPTIONS ─────────────────────────────────────────────────────────
  const categoryOptions = useMemo(() => {
    const statusFiltered = statusFilter === 'all'
      ? safeProducts
      : safeProducts.filter(p => getEffectiveStatus(p) === statusFilter);

    const filtered = categories
      .map(cat => ({
        value: String(cat.id),
        label: cat.name,
        count: statusFiltered.filter(p =>
          String(p.category_id ?? p.categoryId ?? p.categoryID ?? '') === String(cat.id)
        ).length,
      }))
      .filter(opt => opt.count > 0);

    return [
      { value: 'all', label: 'All Categories', count: statusFiltered.length },
      ...filtered,
    ];
  }, [categories, safeProducts, statusFilter, subCategories]);

  // ── SUBCATEGORY OPTIONS ──────────────────────────────────────────────────────
  const subCategoryOptions = useMemo(() => {
    const statusFiltered = statusFilter === 'all'
      ? safeProducts
      : safeProducts.filter(p => getEffectiveStatus(p) === statusFilter);

    const catFiltered = categoryFilter === 'all'
      ? statusFiltered
      : statusFiltered.filter(p =>
          String(p.category_id ?? p.categoryId ?? p.categoryID ?? '') === String(categoryFilter)
        );

    const relevantSubs = categoryFilter === 'all'
      ? subCategories
      : subCategories.filter(s =>
          String(s.category_id ?? s.categoryId ?? '') === String(categoryFilter)
        );

    const filtered = relevantSubs
      .map(sub => ({
        value: String(sub.id),
        label: sub.subcat_name,
        count: catFiltered.filter(p =>
          String(p.sub_category_id ?? p.subcategory_id ?? p.subCategoryId ?? '') === String(sub.id)
        ).length,
      }))
      .filter(opt => opt.count > 0);

    return [
      { value: 'all', label: 'All Sub-Categories', count: catFiltered.length },
      ...filtered,
    ];
  }, [subCategories, safeProducts, statusFilter, categoryFilter, categories]);

  const stockOptions = [
    { value: 'all',          label: 'All Stock Levels' },
    { value: 'in-stock',     label: 'In Stock' },
    { value: 'low-stock',    label: 'Low Stock (≤10)' },
    { value: 'out-of-stock', label: 'Out of Stock' },
  ];

  const sortOptions = [
    { value: 'name',        label: 'Name A-Z' },
    { value: 'name-desc',   label: 'Name Z-A' },
    { value: 'price-low',   label: 'Price: Low to High' },
    { value: 'price-high',  label: 'Price: High to Low' },
    { value: 'stock-high',  label: 'Stock: High to Low' },
    { value: 'stock-low',   label: 'Stock: Low to High' },
    { value: 'rating-high', label: 'Rating: High to Low' },
    { value: 'newest',      label: 'Newest First' },
    { value: 'oldest',      label: 'Oldest First' },
  ];

  // ── AUTO-RESET filters ───────────────────────────────────────────────────────
  useEffect(() => {
    if (categoryFilter === 'all') return;
    const valid = categoryOptions.some(o => String(o.value) === String(categoryFilter));
    if (!valid) { setCategoryFilter('all'); setSubCategoryFilter('all'); }
  }, [categoryOptions, categoryFilter]);

  useEffect(() => {
    if (subCategoryFilter === 'all') return;
    const valid = subCategoryOptions.some(o => String(o.value) === String(subCategoryFilter));
    if (!valid) setSubCategoryFilter('all');
  }, [subCategoryOptions, subCategoryFilter]);

  // ── FILTERED + SORTED LIST ───────────────────────────────────────────────────
  const filteredAndSortedProducts = useMemo(() => {
    let filtered = safeProducts.filter((product) => {
      const catId = product.category_id     ?? product.categoryId     ?? product.categoryID    ?? '';
      const subId = product.sub_category_id ?? product.subcategory_id ?? product.subCategoryId ?? '';

      const matchesSearch =
        searchTerm === '' ||
        product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (Array.isArray(product.tags) && product.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())));

      const effectiveStatus    = getEffectiveStatus(product);
      const matchesStatus      = statusFilter === 'all'      || effectiveStatus === statusFilter;
      const matchesCategory    = categoryFilter === 'all'    || String(catId) === String(categoryFilter);
      const matchesSubCategory = subCategoryFilter === 'all' || String(subId) === String(subCategoryFilter);
      const matchesStock =
        stockFilter === 'all' ||
        (stockFilter === 'in-stock'     && Number(product.stock) > 10) ||
        (stockFilter === 'low-stock'    && Number(product.stock) > 0 && Number(product.stock) <= 10) ||
        (stockFilter === 'out-of-stock' && Number(product.stock) === 0);

      return matchesSearch && matchesStatus && matchesCategory && matchesSubCategory && matchesStock;
    });

    filtered.sort((a, b) => {
      const priceA = Number(a.price) || 0, priceB = Number(b.price) || 0;
      const stockA = Number(a.stock) || 0, stockB = Number(b.stock) || 0;
      switch (sortBy) {
        case 'name':        return (a.name || '').localeCompare(b.name || '');
        case 'name-desc':   return (b.name || '').localeCompare(a.name || '');
        case 'price-low':   return priceA - priceB;
        case 'price-high':  return priceB - priceA;
        case 'stock-high':  return stockB - stockA;
        case 'stock-low':   return stockA - stockB;
        case 'rating-high': return (Number(b.rating) || 0) - (Number(a.rating) || 0);
        case 'newest':      return Number(b.id) - Number(a.id);
        case 'oldest':      return Number(a.id) - Number(b.id);
        default:            return 0;
      }
    });
    return filtered;
  }, [safeProducts, searchTerm, statusFilter, categoryFilter, subCategoryFilter, stockFilter, sortBy, categories, subCategories]);

  // ── STATS ────────────────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const totalProducts      = safeProducts.length;
    const activeProducts     = safeProducts.filter(p => p.status === 'Active').length;
    const totalValue         = safeProducts.reduce((sum, p) => sum + (Number(p.max_retail_price || p.price || 0) * Number(p.stock || 0)), 0);
    const lowStockProducts   = safeProducts.filter(p => Number(p.stock) > 0 && Number(p.stock) <= 10).length;
    const outOfStockProducts = safeProducts.filter(p => Number(p.stock) === 0).length;
    const averageRating      = totalProducts === 0 ? 0 : safeProducts.reduce((sum, p) => sum + (p.rating || 0), 0) / totalProducts;
    return { totalProducts, activeProducts, totalValue, lowStockProducts, outOfStockProducts, averageRating: averageRating.toFixed(1) };
  }, [safeProducts]);

  // ── HANDLERS ─────────────────────────────────────────────────────────────────
  const handleSelectProduct = (id) =>
    setSelectedProducts(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const handleSelectAll = () =>
    setSelectedProducts(
      selectedProducts.length === filteredAndSortedProducts.length ? [] : filteredAndSortedProducts.map(p => p.id)
    );

  const handleDeleteProduct = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    await deleteProduct(id);
    setSelectedProducts(p => p.filter(x => x !== id));
    setActiveDropdown(null);
  };

  /* ── Toggle Status with Toast ── */
  const handleToggleStatus = async (id) => {
    const product = safeProducts.find(p => p.id === id);
    if (!product) return;
    try {
      await toggleProductStatus(id, { ...product, status: product.status === 'Active' ? 'Inactive' : 'Active' });
      const newStatus = product.status === 'Active' ? 'Inactive' : 'Active';
      showToast(`Status updated to ${newStatus} successfully!`);
    } catch (err) {
      console.error('Toggle status failed', err);
      showToast('Failed to update status.', 'error');
    }
    setActiveDropdown(null);
  };

  const handleEditProduct = (product) => {
    navigate(`/products/edit/${product.id}`);
    setActiveDropdown(null);
  };

  const getProductImage = (product) => {
    if (!product) return '';
    if (Array.isArray(product.images) && product.images.length > 0) return product.images[0];
    return product.image || product.image_url || product.imageUrl || product.thumbnail || product.photo || '';
  };

  if (loading || categoriesLoading || subCategoriesLoading)
    return <div className="p-10 text-center">Loading...</div>;
  if (error) return <div className="p-10 text-center text-red-600">{error}</div>;

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        {/* Page Header */}
        <header className="flex flex-col sm:flex-row gap-4 sm:gap-5 justify-between items-start sm:items-center mb-8">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-teal-600 rounded-xl flex items-center justify-center">
              <Package className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-teal-800">Products</h1>
              <p className="text-gray-600 mt-1">Manage your product inventory</p>
            </div>
          </div>
          <button
            onClick={() => navigate('/products/add')}
             className="w-full sm:w-auto flex items-center justify-center gap-2 bg-teal-600 text-white px-5 py-2.5 sm:px-6 sm:py-3 rounded-xl hover:bg-teal-700 transition-colors font-medium text-sm sm:text-base"
          >
            <Plus className="w-5 h-5" />
            Add Product
          </button>
        </header>

        {/* Statistics Cards */}
        <section className="grid grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {[
            { label: 'Total Products',  value: stats.totalProducts,                     icon: Package,   iconBg: 'bg-teal-100',   iconColor: 'text-teal-600',   valueColor: 'text-teal-800' },
            { label: 'Active Products', value: stats.activeProducts,                    icon: BarChart3, iconBg: 'bg-green-100',  iconColor: 'text-green-600',  valueColor: 'text-green-600' },
            { label: 'Inventory Value', value: `₹${stats.totalValue.toLocaleString()}`, icon: BarChart3, iconBg: 'bg-orange-100', iconColor: 'text-orange-600', valueColor: 'text-orange-600' },
          ].map(({ label, value, icon: Icon, iconBg, iconColor, valueColor }) => (
            <div key={label} className="bg-white bg-opacity-80 rounded-xl border border-orange-200 p-6 hover:shadow-lg transition-all">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1">{label}</p>
                  <p className={`text-2xl font-bold ${valueColor}`}>{value}</p>
                </div>
                <div className={`w-12 h-12 ${iconBg} rounded-lg flex items-center justify-center`}>
                  <Icon className={`w-6 h-6 ${iconColor}`} />
                </div>
              </div>
            </div>
          ))}
        </section>

        {/* Search and Filters */}
        <section className="bg-white bg-opacity-60 rounded-xl border border-orange-200 p-6 mb-8 hover:shadow-2xl hover:shadow-orange-200/50 transition-all duration-300">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search products by name, SKU, or tags..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 text-teal-600 hover:text-teal-800 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 rounded-lg p-2 border border-teal-200 bg-teal-50"
            >
              <Filter className="w-5 h-5" />
              <span className="font-medium">Filters</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </button>
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mt-4 pt-4 border-t border-gray-200">
              {[
                {
                  label: 'Status',
                  value: statusFilter,
                  onChange: (v) => { setStatusFilter(v); setCategoryFilter('all'); setSubCategoryFilter('all'); },
                  options: statusOptions,
                },
                {
                  label: 'Category',
                  value: categoryFilter,
                  onChange: (v) => { setCategoryFilter(v); setSubCategoryFilter('all'); },
                  options: categoryOptions,
                },
                {
                  label: 'Sub-Category',
                  value: subCategoryFilter,
                  onChange: setSubCategoryFilter,
                  options: subCategoryOptions,
                },
                { label: 'Stock Level', value: stockFilter, onChange: setStockFilter, options: stockOptions },
                { label: 'Sort By',     value: sortBy,      onChange: setSortBy,      options: sortOptions },
              ].map(({ label, value, onChange, options }) => (
                <div key={label}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
                  <select
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400"
                  >
                    {options.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}{opt.count !== undefined ? ` (${opt.count})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          )}

          {selectedProducts.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-between">
              <span className="text-sm text-gray-600">{selectedProducts.length} product(s) selected</span>
              <div className="flex gap-2">
                <button className="px-3 py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors text-sm">
                  Delete Selected
                </button>
                <button
                  className="px-3 py-2 bg-orange-50 text-orange-700 rounded-lg hover:bg-orange-100 transition-colors text-sm"
                  onClick={async () => {
                    for (const id of selectedProducts) {
                      const p = safeProducts.find(p => p.id === id);
                      if (!p) continue;
                      try {
                        await toggleProductStatus(id, { ...p, status: p.status === 'Active' ? 'Inactive' : 'Active' });
                      } catch (e) { console.error(e); }
                    }
                    showToast('Bulk status updated successfully!');
                  }}
                >
                  Toggle Status
                </button>
              </div>
            </div>
          )}
        </section>

        {/* Products Section */}
        <section>
          {filteredAndSortedProducts.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-24 h-24 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
                <Package className="w-12 h-12 text-gray-400" />
              </div>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">No products found</h2>
              <p className="text-gray-600 mb-6">
                {searchTerm || statusFilter !== 'all' || categoryFilter !== 'all' || stockFilter !== 'all'
                  ? 'Try adjusting your search or filter criteria.'
                  : 'Create your first product to get started.'}
              </p>
              <button
                onClick={() => navigate('/products/add')}
                className="bg-teal-600 text-white px-6 py-3 rounded-xl hover:bg-teal-700 transition-colors font-medium"
              >
                Add Product
              </button>
            </div>
          ) : (
            <>
              {/* Count + View Toggle */}
              <div className="flex items-center justify-between mb-5">
                <span className="text-sm text-gray-500">
                  Showing <span className="font-semibold text-teal-700">{filteredAndSortedProducts.length}</span> of{' '}
                  <span className="font-semibold text-gray-700">{products.length}</span> Products
                </span>
                <div className="flex items-center gap-1 p-1 rounded-lg">
                  {[
                    { mode: 'grid',  Icon: Grid3X3, title: 'Grid' },
                    { mode: 'list',  Icon: Layers,  title: 'List' },
                    { mode: 'table', Icon: Table,   title: 'Table' },
                  ].map(({ mode, Icon, title }) => (
                    <button
                      key={mode}
                      onClick={() => setViewMode(mode)}
                      title={title}
                      className={`p-2 rounded-md transition-all ${
                        viewMode === mode ? 'bg-teal-600 text-white shadow-sm' : 'text-gray-500 hover:text-teal-600 hover:bg-white'
                      }`}
                    >
                      <Icon size={17} />
                    </button>
                  ))}
                </div>
              </div>

              {/* ── GRID VIEW ── */}
              {viewMode === 'grid' && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {filteredAndSortedProducts.map((product) => (
                    <ProductCard
                      key={product.id}
                      product={product}
                      categories={categories}
                      subCategories={subCategories}
                      isSelected={selectedProducts.includes(product.id)}
                      onSelect={handleSelectProduct}
                      onEdit={handleEditProduct}
                      onDelete={handleDeleteProduct}
                      onToggleStatus={handleToggleStatus}
                      activeDropdown={activeDropdown}
                      setActiveDropdown={setActiveDropdown}
                    />
                  ))}
                </div>
              )}

              {/* ── LIST VIEW ── */}
              {viewMode === 'list' && (
                <div className="flex flex-col gap-3">
                  {filteredAndSortedProducts.map((product) => {
                    const img = getProductImage(product);
                    const category = categories.find(c => String(c.id) === String(product.category_id ?? product.categoryId ?? ''));
                    return (
                      <div
                        key={product.id}
                        className="bg-white rounded-xl border border-gray-200 hover:border-teal-300 hover:shadow-md transition-all duration-200 p-4"
                      >
                        <div className="flex items-center gap-4">
                          <div className="relative flex-shrink-0">
                            <img
                              src={img || `https://via.placeholder.com/60?text=No+Img`}
                              alt={product.name}
                              className="w-14 h-14 rounded-lg object-cover border border-gray-100"
                            />
                            {product.status !== 'Active' && (
                              <div className="absolute inset-0 bg-gray-200 bg-opacity-60 rounded-lg" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-gray-800 text-sm leading-snug truncate max-w-xs">{product.name}</h3>
                            <div className="flex items-center gap-3 mt-1 flex-wrap">
                              {product.sku && (
                                <span className="text-xs text-gray-400 font-mono bg-gray-50 px-1.5 py-0.5 rounded">{product.sku}</span>
                              )}
                              {category && (
                                <span className="text-xs text-teal-600 bg-teal-50 px-2 py-0.5 rounded-full">{category.name}</span>
                              )}
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0 min-w-[80px]">
                            <p className="text-base font-bold text-gray-800">₹{Number(product.price).toLocaleString()}</p>
                            {product.max_retail_price && Number(product.max_retail_price) !== Number(product.price) && (
                              <p className="text-xs text-gray-400 line-through">₹{Number(product.max_retail_price).toLocaleString()}</p>
                            )}
                          </div>
                          <div className="flex-shrink-0 min-w-[120px] text-center">
                            <StockBadge stock={product.stock} />
                          </div>
                          <div className="flex-shrink-0">
                            <ActionButtons product={product} onEdit={handleEditProduct} onToggle={handleToggleStatus} onDelete={handleDeleteProduct} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* ── TABLE VIEW ── */}
              {viewMode === 'table' && (
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm min-w-[520px]">
                      <thead>
                        <tr className="bg-gradient-to-r from-teal-50 to-orange-50 border-b border-gray-200">
                          <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide w-12">Img</th>
                          <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Product</th>
                          <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide hidden md:table-cell">Category</th>
                          <th className="px-3 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Price</th>
                          <th className="px-3 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide">Stock</th>
                          <th className="px-3 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {filteredAndSortedProducts.map((product, idx) => {
                          const img = getProductImage(product);
                          const category = categories.find(c => String(c.id) === String(product.category_id ?? product.categoryId ?? ''));
                          return (
                            <tr
                              key={product.id}
                              className={`hover:bg-teal-50/40 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}
                            >
                              <td className="px-3 py-2">
                                <img
                                  src={img || `https://via.placeholder.com/40?text=?`}
                                  alt={product.name}
                                  className="w-9 h-9 rounded-lg object-cover border border-gray-100"
                                />
                              </td>
                              <td className="px-3 py-2 max-w-[160px]">
                                <p className="font-medium text-gray-800 text-sm truncate">{product.name}</p>
                                {product.sku && <p className="text-xs text-gray-400 font-mono mt-0.5">{product.sku}</p>}
                                {category && (
                                  <span className="md:hidden text-xs text-teal-700 bg-teal-50 px-1.5 py-0.5 rounded-full mt-0.5 inline-block">{category.name}</span>
                                )}
                              </td>
                              <td className="px-3 py-2 hidden md:table-cell">
                                {category ? (
                                  <span className="text-xs text-teal-700 bg-teal-50 border border-teal-100 px-2 py-1 rounded-full">{category.name}</span>
                                ) : (
                                  <span className="text-xs text-gray-400">—</span>
                                )}
                              </td>
                              <td className="px-3 py-2 text-right whitespace-nowrap">
                                <p className="font-semibold text-gray-800 text-sm">₹{Number(product.price).toLocaleString()}</p>
                                {product.max_retail_price && Number(product.max_retail_price) !== Number(product.price) && (
                                  <p className="text-xs text-gray-400 line-through">₹{Number(product.max_retail_price).toLocaleString()}</p>
                                )}
                              </td>
                              <td className="px-3 py-2 text-center">
                                <StockBadge stock={product.stock} />
                              </td>
                              <td className="px-3 py-2">
                                <div className="flex items-center justify-center">
                                  <ActionButtons product={product} onToggle={handleToggleStatus} onEdit={handleEditProduct} onDelete={handleDeleteProduct} />
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between flex-wrap gap-2">
                    <p className="text-xs text-gray-500">
                      {filteredAndSortedProducts.length} product{filteredAndSortedProducts.length !== 1 ? 's' : ''}
                    </p>
                    <p className="text-xs text-gray-400">
                      Total value:{' '}
                      <span className="font-semibold text-orange-600">
                        ₹{filteredAndSortedProducts.reduce((s, p) => s + Number(p.price || 0) * Number(p.stock || 0), 0).toLocaleString()}
                      </span>
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
        </section>
      </div>

      {activeDropdown && (
        <div className="fixed inset-0 z-40" onClick={() => setActiveDropdown(null)} />
      )}

      {/* ── Toast Notification ── */}
      <div
        style={{
          position: 'fixed',
          bottom: '24px',
          right: '24px',
          zIndex: 9999,
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          opacity: toast.visible ? 1 : 0,
          transform: toast.visible ? 'translateY(0) scale(1)' : 'translateY(16px) scale(0.97)',
          pointerEvents: toast.visible ? 'auto' : 'none',
        }}
      >
        <div
          className={`flex items-center gap-3 px-5 py-3.5 rounded-xl shadow-lg text-sm font-medium ${
            toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-teal-600 text-white'
          }`}
        >
          {toast.type === 'error' ? (
            <svg className="w-5 h-5 shrink-0" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
            </svg>
          ) : (
            <CheckCircle className="w-5 h-5 shrink-0" />
          )}
          {toast.message}
        </div>
      </div>
    </div>
  );
};

export default ProductPage;